/**
 * Explore Unknown mission 40 — centered Kangaroo evolution art during bot turns.
 * Server sets `gameState.exploreUnknownMission40KangarooCenterImageSrc` on each bot turn start.
 *
 * Each center-art change plays an old-TV channel zap (static + horizontal glowing roll bar).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ExploreUnknownBossKangarooOverlay.css';

/** Old-TV channel zap length (static + rolling bar + snap-in). */
const KANGAROO_CHANNEL_ZAP_MS = 580;

function encodePublicImageSrc(path) {
  if (!path || typeof path !== 'string') {
    throw new Error('ExploreUnknownBossKangarooOverlay: image path is required');
  }
  if (!path.startsWith('/images/')) {
    throw new Error(`ExploreUnknownBossKangarooOverlay: path must start with /images/ (${path})`);
  }
  const prefix = '/images/';
  const rest = path.slice(prefix.length);
  return `${prefix}${rest.split('/').map((seg) => encodeURIComponent(seg)).join('/')}`;
}

function isBotActiveTurn(turn) {
  if (turn == null) return false;
  const t = String(turn);
  return t === 'bot' || t === 'campaign_bot' || t.startsWith('bot');
}

export default function ExploreUnknownBossKangarooOverlay({ gameState }) {
  const missionIndex = Number(gameState?.exploreUnknownMissionIndex);
  const imageSrc = gameState?.exploreUnknownMission40KangarooCenterImageSrc;
  const isBotTurn = useMemo(() => isBotActiveTurn(gameState?.turn), [gameState?.turn]);

  const encodedSrc = useMemo(
    () => (imageSrc ? encodePublicImageSrc(imageSrc) : null),
    [imageSrc]
  );

  const [displaySrc, setDisplaySrc] = useState(null);
  const [outgoingSrc, setOutgoingSrc] = useState(null);
  const [isZapping, setIsZapping] = useState(false);
  const [zapNonce, setZapNonce] = useState(0);
  const lastEncodedRef = useRef(null);
  const displaySrcRef = useRef(null);
  const zapTimerRef = useRef(null);
  const zapGenerationRef = useRef(0);

  useEffect(() => {
    displaySrcRef.current = displaySrc;
  }, [displaySrc]);

  useEffect(() => {
    if (!encodedSrc) {
      // Keep lastEncodedRef so a transient missing field does not re-classify the
      // next real frame as a "first appearance" (which used to skip the zap).
      if (zapTimerRef.current != null) {
        clearTimeout(zapTimerRef.current);
        zapTimerRef.current = null;
      }
      setDisplaySrc(null);
      setOutgoingSrc(null);
      setIsZapping(false);
      return undefined;
    }

    const previous = lastEncodedRef.current;
    if (previous === encodedSrc) {
      if (displaySrcRef.current == null) {
        setDisplaySrc(encodedSrc);
      }
      return undefined;
    }

    lastEncodedRef.current = encodedSrc;
    const outgoing = displaySrcRef.current || previous;
    const generation = zapGenerationRef.current + 1;
    zapGenerationRef.current = generation;

    setOutgoingSrc(outgoing);
    setIsZapping(true);
    setZapNonce(generation);
    // Keep showing the previous frame until the zap finishes; incoming img uses encodedSrc while zapping.
    if (displaySrcRef.current == null && outgoing == null) {
      setDisplaySrc(encodedSrc);
    }

    if (zapTimerRef.current != null) {
      clearTimeout(zapTimerRef.current);
    }
    zapTimerRef.current = window.setTimeout(() => {
      if (zapGenerationRef.current !== generation) {
        return;
      }
      setDisplaySrc(encodedSrc);
      setOutgoingSrc(null);
      setIsZapping(false);
      zapTimerRef.current = null;
    }, KANGAROO_CHANNEL_ZAP_MS);

    return () => {
      if (zapTimerRef.current != null && zapGenerationRef.current === generation) {
        clearTimeout(zapTimerRef.current);
        zapTimerRef.current = null;
      }
    };
  }, [encodedSrc]);

  if (missionIndex !== 40 || !displaySrc) {
    return null;
  }

  return createPortal(
    <div
      className={`explore-unknown-boss-kangaroo-overlay${isBotTurn ? ' is-bot-turn' : ''}`}
      aria-hidden="true"
    >
      <div className={`explore-unknown-boss-kangaroo-stage${isZapping ? ' is-zapping' : ''}`}>
        {isZapping && outgoingSrc ? (
          <img
            className="explore-unknown-boss-kangaroo-image explore-unknown-boss-kangaroo-image--outgoing"
            src={outgoingSrc}
            alt=""
            draggable={false}
          />
        ) : null}
        <img
          className={`explore-unknown-boss-kangaroo-image explore-unknown-boss-kangaroo-image--incoming${
            isZapping ? ' is-zapping-in' : ''
          }`}
          src={isZapping && encodedSrc ? encodedSrc : displaySrc}
          alt=""
          draggable={false}
        />
        {isZapping ? (
          <>
            <div className="explore-unknown-boss-kangaroo-tv-static" aria-hidden="true" />
            <div
              key={`kangaroo-tv-roll-${zapNonce}`}
              className="explore-unknown-boss-kangaroo-tv-roll"
              aria-hidden="true"
            />
          </>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
